//
// Copyright (c) 2011-2024 by Curalate, Inc
//

import React, { useState } from 'react';

import {
  IContentLibraryFiltersOptions,
  IContentLibrarySourcesOptions,
} from '@apis/content-library/contentLibraryApiModel';
import HelpIcon from '@components/atoms/help-icon/HelpIcon';
import HR from '@components/atoms/hr/HR';
import { CaptionErrorText } from '@components/atoms/text/Text';
import { ChipSet } from '@components/media-filters/ChipSet';
import { CollapsibleSection } from '@components/media-filters/CollapsibleSection';
import {
  FilterSection,
  FilterSectionTitle,
} from '@components/media-filters/filterSectionModels';
import { MediaLibrarySearchTagPrefixes } from '@components/media-filters/mediaFilterConstants';
import { MediaFilterTrackingIds } from '@components/media-filters/mediaFilterTrackingIds';
import { useGetVisualTagsSuggestionsQuery } from '@components/media-filters/useGetVisualTagsSuggestionsQuery';
import { SearchBarWithTypeAhead } from '@components/molecules/search-bar-with-type-ahead/SearchBarWithTypeAhead';
import SparklesColor from '@images/sparkles-color.svg';

type SearchResult = React.ComponentProps<
  typeof SearchBarWithTypeAhead
>['typeAheadResults'][number];

export const DEFAULT_VISUAL_TAGS_FILTER_SECTION: FilterSection<string> = {
  options: [],
  paramKey: 'semanticTags',
};

export interface VisualTagsFilterProps {
  filters: IContentLibraryFiltersOptions;
  isInitiallyCollapsed?: boolean;
  onChange: (section: FilterSection<string>) => void;
  section: FilterSection<string> | undefined;
  sources: IContentLibrarySourcesOptions;
}

export const VisualTagsFilter = ({
  filters,
  isInitiallyCollapsed,
  onChange,
  section = DEFAULT_VISUAL_TAGS_FILTER_SECTION,
  sources,
}: VisualTagsFilterProps) => {
  const [searchTerm, setSearchTerm] = useState<string>('');

  const { data, isError, isFetching } = useGetVisualTagsSuggestionsQuery({
    filters,
    sortByDistanceTo: searchTerm,
    sources,
  });

  const visualTags: SearchResult[] = data ?? [];

  const selectedVisualTags = section.options.filter(
    ({ isSelected }) => isSelected,
  );

  const handleSelect = async ({ label, prefix }: SearchResult) => {
    // Listed options include selected tags. Skip if tag is already selected.
    if (selectedVisualTags.some(({ value }) => value === label)) {
      return;
    }

    const option = {
      isSelected: true,
      label: prefix + label,
      value: label,
    };

    onChange({
      ...section,
      options: [...section.options, option],
    });
  };

  return (
    <>
      <CollapsibleSection
        isCollapsed={isInitiallyCollapsed}
        leftComponent={<img src={SparklesColor} />}
        rightComponent={
          <HelpIcon
            helpText="Filter based on visual tags, which we've identified using AI. Try searching for an object or color, or click the searchbox to see common tags."
            placement='left'
          />
        }
        title={FilterSectionTitle.semanticTags}
      >
        <SearchBarWithTypeAhead
          ellipsize={1}
          isLoadingTypeAheadResults={isFetching}
          getTypeAheadResults={async (searchTerm: string | undefined) =>
            setSearchTerm(searchTerm ?? '')
          }
          onSearchSelection={handleSelect}
          resultsLabel='Posts'
          searchBarPlaceholderText=''
          searchPrefixes={[
            MediaLibrarySearchTagPrefixes.igHashtags,
            MediaLibrarySearchTagPrefixes.igUsernames,
          ]}
          shouldEnableSearchTermFiltering={false}
          trackingIdPrefix={
            MediaFilterTrackingIds.VISUAL_TAGS_FILTER_SEARCH_BAR
          }
          typeAheadResults={visualTags}
        />

        {isError && (
          <CaptionErrorText>
            An error occurred retrieving your visual tags. Please try again or
            contact Bazaarvoice Support.
          </CaptionErrorText>
        )}

        {selectedVisualTags.length > 0 && (
          <ChipSet
            options={selectedVisualTags}
            onClick={(option) => {
              option.isSelected = false;
              onChange(section);
            }}
            trackingId={MediaFilterTrackingIds.VISUAL_TAGS_FILTER_CHIP_SET}
          />
        )}
      </CollapsibleSection>
      <HR />
    </>
  );
};
